import { CONVEYANCE_TYPES } from '@koala/sdk';
import { useRouter } from 'next/router';
import { styled } from '@/components/content/stitches';
import {
  CHECKOUT_HANDOFF_ANCHOR_ID,
  CONFIRM_PICKUP_COPY,
} from '@/features/checkout/confirm-pickup-location/constants';
import { API_CONVEYANCE_TYPES } from '@/constants/events';
import { ROUTES } from '@/constants/routes';
import { LocationPicker } from '@/features/location-pill/location-picker';
import { useSelector } from '@/redux';
import { getStoreTimeInfo } from '@/utils/locations';

/**
 * Persistent pickup-location indicator shown in the header throughout checkout
 * (TEAM-13771). Shows the active store as the Location Pill, which opens the
 * Select Location pop-up for multi-location brands (TEAM-13315), and — when the
 * store is closed — a "Select a time" affordance that scrolls to the checkout
 * time picker.
 *
 * Scoped to pickup (non-delivery) orders on the checkout page: for delivery the
 * store is implied by the delivery address, so there's no pickup location to
 * confirm or change here.
 */
export function CheckoutLocationIndicator() {
  const router = useRouter();
  const { location, checkoutBasket, fulfillment } = useSelector((state) => state.app.basket);

  const conveyanceType = checkoutBasket?.conveyance_type?.type;
  // The remote checkout basket only exists once the order is initialized (from the cart's
  // Checkout button); after a reload of /checkout it is null. The local basket still knows
  // the order is a delivery, so a delivery order never gets the pickup pill (TEAM-13315 AC16).
  const isDelivery =
    conveyanceType === API_CONVEYANCE_TYPES.DELIVERY ||
    fulfillment?.type === CONVEYANCE_TYPES.DELIVERY;
  const isCheckout = router.pathname === ROUTES.CHECKOUT;

  if (!isCheckout || isDelivery || !location?.id) {
    return null;
  }

  const { isOpen } = getStoreTimeInfo(location, conveyanceType);

  const selectTime = () => {
    document
      .getElementById(CHECKOUT_HANDOFF_ANCHOR_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <Bar data-testid="checkout-location-indicator">
      <LocationPicker location={location} accentPin />
      {!isOpen && (
        <ActionButton type="button" data-testid="indicator-select-time" onClick={selectTime}>
          {CONFIRM_PICKUP_COPY.selectTime}
        </ActionButton>
      )}
    </Bar>
  );
}

const Bar = styled('div', {
  alignItems: 'center',
  backgroundColor: '$shade200',
  borderBottom: '1px solid $shade300',
  // The pill inherits its text colour; pin it so brand body colours stay legible on this grey bar.
  color: '$shade900',
  display: 'flex',
  gap: '$3',
  justifyContent: 'space-between',
  padding: '$2 $4',
  position: 'sticky',
  top: '$12',
  width: '100%',
  zIndex: 10,

  '@md': {
    padding: '$2 $10',
    top: '$20',
  },
});

const ActionButton = styled('button', {
  background: 'none',
  border: 'none',
  color: '$accentPrimary',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 600,
  padding: 0,
  whiteSpace: 'nowrap',
});
